<!-- src/components/SupportInfo.vue -->
<template>
  <div class="support-container min-h-screen bg-slate-50 dark:bg-gray-950 p-4 lg:p-8">
    <!-- Hero Section -->
    <header class="relative overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-600 via-blue-600 to-purple-700 p-8 lg:p-12 mb-8 shadow-2xl shadow-indigo-500/20">
      <div class="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -mr-20 -mt-20"></div>
      <div class="absolute bottom-0 left-0 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl -ml-10 -mb-10"></div>
      
      <div class="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8">
        <div class="text-center lg:text-left space-y-4 max-w-2xl">
          <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white text-xs font-semibold uppercase tracking-wider animate-fade-in">
            <Icon icon="mdi:shield-check" class="text-sm" />
            Centro de Soporte Oficial
          </div>
          <h1 class="text-4xl lg:text-6xl font-black text-white leading-tight tracking-tight">
            ¿Cómo podemos <span class="text-indigo-200">ayudarte</span> hoy?
          </h1>
          <p class="text-indigo-100 text-lg opacity-90 font-medium">
            Estamos aquí para resolver tus dudas técnicas y garantizar que tu sistema funcione a la perfección.
          </p>
          
          <!-- Mockup Search Bar -->
          <div class="relative mt-8 group max-w-md mx-auto lg:mx-0">
            <Icon icon="mdi:magnify" class="absolute left-4 top-1/2 -translate-y-1/2 text-indigo-300 text-xl group-focus-within:text-white transition-colors" />
            <input 
              type="text" 
              placeholder="Busca soluciones o preguntas frecuentes..." 
              class="w-full bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl py-4 pl-12 pr-4 text-white placeholder-indigo-200 outline-none focus:ring-4 focus:ring-white/20 focus:bg-white/20 transition-all shadow-inner"
            >
          </div>
        </div>

        <div class="hidden lg:block relative group">
          <div class="absolute inset-0 bg-white/20 rounded-full blur-2xl group-hover:bg-white/30 transition-all duration-700"></div>
          <img 
            :src="logoUrl" 
            alt="Jean Informático Logo" 
            class="relative w-64 h-40 object-contain drop-shadow-2xl transform group-hover:scale-105 transition-transform duration-500"
            @error="handleImageError"
          >
        </div>
      </div>
    </header>

    <div class="grid lg:grid-cols-3 gap-8">
      <!-- Left Column: Quick Contact & Info -->
      <div class="lg:col-span-1 space-y-6">
        <h2 class="text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
          <Icon icon="mdi:phone-message" class="text-indigo-600" />
          Canales de Atención
        </h2>
        
        <div class="grid gap-4">
          <!-- WhatsApp Card -->
          <a href="https://wa.me/584125696219" target="_blank" class="contact-card group">
            <div class="card-icon bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600">
              <Icon icon="mdi:whatsapp" />
            </div>
            <div class="flex-1">
              <h3 class="font-bold text-slate-800 dark:text-white">WhatsApp Soporte</h3>
              <p class="text-sm text-slate-500 dark:text-gray-400">Respuesta rápida e inmediata</p>
            </div>
            <Icon icon="mdi:chevron-right" class="text-slate-400 group-hover:text-emerald-500 transition-colors" />
          </a>

          <!-- Email Card -->
          <a href="mailto:soportejeaninformatico@gmail.com" class="contact-card group">
            <div class="card-icon bg-blue-100 dark:bg-blue-900/30 text-blue-600">
              <Icon icon="mdi:email-outline" />
            </div>
            <div class="flex-1">
              <h3 class="font-bold text-slate-800 dark:text-white">Correo Electrónico</h3>
              <p class="text-sm text-slate-500 dark:text-gray-400">soportejeaninformatico@gmail.com</p>
            </div>
            <Icon icon="mdi:chevron-right" class="text-slate-400 group-hover:text-blue-500 transition-colors" />
          </a>

          <!-- Remote Assist -->
          <div class="contact-card bg-slate-800 dark:bg-slate-900 border-none">
            <div class="card-icon bg-white/10 text-white">
              <Icon icon="mdi:remote-desktop" />
            </div>
            <div class="flex-1">
              <h3 class="font-bold text-white">Asistencia Remota</h3>
              <p class="text-sm text-slate-400">Acceso mediante AnyDesk o TeamViewer</p>
              <div class="flex gap-2 mt-3">
                <button class="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-1">
                  <Icon icon="mdi:download" /> AnyDesk
                </button>
                <button class="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-lg transition-colors flex items-center gap-1">
                  <Icon icon="mdi:download" /> TeamViewer
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- System Stats -->
        <div class="p-6 rounded-3xl bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 shadow-sm">
          <h3 class="font-bold text-slate-800 dark:text-white mb-4 flex items-center gap-2 text-sm uppercase tracking-wider">
            <span class="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
            Estado del Sistema
          </h3>
          <div class="space-y-4">
            <div class="flex justify-between items-center text-sm">
              <span class="text-slate-600 dark:text-gray-400">Base de Datos</span>
              <span class="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">OPERATIVO</span>
            </div>
            <div class="flex justify-between items-center text-sm">
              <span class="text-slate-600 dark:text-gray-400">Servidor API</span>
              <span class="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">OPERATIVO</span>
            </div>
            <div class="flex justify-between items-center text-sm">
              <span class="text-slate-600 dark:text-gray-400">Módulo Facturación</span>
              <span class="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold">OPERATIVO</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Ticket Form & FAQ -->
      <div class="lg:col-span-2 space-y-8">
        <!-- Ticket Form Section -->
        <section class="bg-white dark:bg-gray-900 rounded-3xl border border-slate-200 dark:border-gray-800 shadow-sm overflow-hidden">
          <div class="p-6 lg:p-8 bg-slate-50/50 dark:bg-gray-800/50 border-b border-slate-200 dark:border-gray-800">
            <h2 class="text-2xl font-bold text-slate-800 dark:text-white">Enviar Ticket de Soporte</h2>
            <p class="text-slate-500 dark:text-gray-400 mt-1">Describe tu problema y te contactaremos a la brevedad.</p>
          </div>
          
          <form @submit.prevent="handleSubmit" class="p-6 lg:p-8 space-y-6">
            <div class="grid md:grid-cols-2 gap-6">
              <div class="space-y-2">
                <label class="text-sm font-bold text-slate-700 dark:text-gray-300">Tipo de Incidencia</label>
                <select v-model="form.type" class="form-input">
                  <option value="tecnico">Problema Técnico</option>
                  <option value="facturacion">Duda sobre Facturación</option>
                  <option value="capacitacion">Capacitación / Uso</option>
                  <option value="sugerencia">Sugerencia de Mejora</option>
                </select>
              </div>
              <div class="space-y-2">
                <label class="text-sm font-bold text-slate-700 dark:text-gray-300">Prioridad</label>
                <div class="flex gap-2">
                  <button 
                    type="button" 
                    v-for="p in ['Baja', 'Media', 'Alta']" 
                    :key="p"
                    @click="form.priority = p"
                    :class="[
                      'flex-1 py-2 rounded-xl text-xs font-bold transition-all border',
                      form.priority === p 
                        ? 'bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-500/20' 
                        : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:bg-slate-50 dark:hover:bg-gray-700'
                    ]"
                  >
                    {{ p }}
                  </button>
                </div>
              </div>
            </div>

            <div class="space-y-2">
              <label class="text-sm font-bold text-slate-700 dark:text-gray-300">Asunto</label>
              <input v-model="form.subject" type="text" placeholder="Resumen corto del problema" class="form-input">
            </div>

            <div class="space-y-2">
              <label class="text-sm font-bold text-slate-700 dark:text-gray-300">Descripción detallada</label>
              <textarea v-model="form.message" rows="4" placeholder="Explica detalladamente qué sucede..." class="form-input resize-none"></textarea>
            </div>

            <button 
              type="submit" 
              :disabled="submitting"
              class="w-full bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white font-bold py-4 rounded-2xl shadow-xl shadow-indigo-500/25 transition-all transform hover:-translate-y-1 active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 disabled:transform-none"
            >
              <Icon v-if="submitting" icon="mdi:loading" class="animate-spin" />
              <Icon v-else icon="mdi:send" />
              {{ submitting ? 'Enviando...' : 'Enviar Solicitud de Soporte' }}
            </button>
          </form>
        </section>

        <!-- FAQ Section -->
        <section class="space-y-6">
          <h2 class="text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
            <Icon icon="mdi:comment-question" class="text-indigo-600" />
            Preguntas Frecuentes
          </h2>
          
          <div class="space-y-4">
            <div v-for="(faq, i) in faqs" :key="i" class="faq-item">
              <button 
                @click="toggleFaq(i)"
                class="w-full flex items-center justify-between p-5 text-left focus:outline-none"
              >
                <span class="font-bold text-slate-700 dark:text-gray-200">{{ faq.q }}</span>
                <Icon 
                  icon="mdi:chevron-down" 
                  :class="['text-xl text-slate-400 transition-transform duration-300', activeFaq === i ? 'rotate-180' : '']" 
                />
              </button>
              <div 
                class="overflow-hidden transition-all duration-300"
                :style="{ maxHeight: activeFaq === i ? '200px' : '0' }"
              >
                <p class="px-5 pb-5 text-slate-600 dark:text-gray-400 text-sm leading-relaxed">
                  {{ faq.a }}
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>

    <!-- Footer Branding -->
    <footer class="mt-16 text-center py-10 border-t border-slate-200 dark:border-gray-800">
      <div class="inline-flex items-center gap-3 p-4 rounded-3xl bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 shadow-sm">
        <img :src="logoUrl" alt="Jean Logo" class="w-12 h-12 object-contain opacity-50 grayscale hover:grayscale-0 hover:opacity-100 transition-all cursor-pointer">
        <div class="text-left">
          <p class="text-xs font-bold text-slate-500 dark:text-gray-500 uppercase tracking-tighter">Desarrollado con ❤️ por</p>
          <p class="text-sm font-black text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">Jean Informático C.A.</p>
        </div>
      </div>
      <p class="mt-4 text-xs text-slate-400 dark:text-gray-600">© 2026 Todos los derechos reservados. Sistema de Gestión Jean Informático.</p>
    </footer>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";

export default {
  name: 'SupportInfo',
  components: {
    Icon
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      logoUrl: require('@/assets/logo/jj.png'),
      imageError: false,
      submitting: false,
      activeFaq: 0,
      form: {
        type: 'tecnico',
        priority: 'Media',
        subject: '',
        message: ''
      },
      faqs: [
        { q: '¿Cómo recupero mi contraseña?', a: 'En la pantalla de inicio de sesión, haz clic en "¿Olvidaste tu contraseña?". Recibirás un enlace de recuperación en tu correo registrado.' },
        { q: '¿El sistema funciona sin internet?', a: 'Sí, la aplicación local permite gestionar ventas e inventario offline. Los datos se sincronizarán cuando la conexión se restablezca.' },
        { q: '¿Cómo genero reportes mensuales?', a: 'Dirígete al módulo de Gestión -> Reportes. Allí podrás filtrar por fechas y descargar en formato PDF o Excel.' },
        { q: '¿Cómo agrego un nuevo técnico?', a: 'En Seguridad -> Usuarios, puedes crear un nuevo usuario y asignarle el rol de "Técnico" para que tenga los permisos adecuados.' }
      ]
    };
  },
  methods: {
    handleImageError() {
      this.imageError = true;
      console.warn('Error al cargar el logo. Verifica la ruta: @/assets/logo-jean.jpg');
    },
    toggleFaq(index) {
      this.activeFaq = this.activeFaq === index ? null : index;
    },
    async handleSubmit() {
      if (!this.form.subject || !this.form.message) {
        this.toast.warning("Por favor completa los campos obligatorios.");
        return;
      }

      this.submitting = true;
      
      // Simulación de envío
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      this.toast.success("¡Ticket enviado con éxito! Nos contactaremos pronto.");
      
      // Reset form
      this.form = {
        type: 'tecnico',
        priority: 'Media',
        subject: '',
        message: ''
      };
      this.submitting = false;
    }
  }
};
</script>

<style scoped>
.support-container {
  font-family: 'Inter', sans-serif;
}

.contact-card {
  @apply flex items-center gap-4 p-4 rounded-3xl bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 shadow-sm hover:shadow-xl hover:shadow-indigo-500/5 transition-all transform hover:-translate-y-1;
}

.card-icon {
  @apply w-12 h-12 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0;
}

.form-input {
  @apply w-full bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-2xl p-4 outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white dark:focus:bg-gray-700 transition-all text-slate-800 dark:text-white;
}

.faq-item {
  @apply bg-white dark:bg-gray-900 border border-slate-200 dark:border-gray-800 rounded-3xl overflow-hidden;
}

@keyframes fade-in {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fade-in {
  animation: fade-in 0.8s ease-out forwards;
}

/* Custom Scrollbar for better aesthetics */
::-webkit-scrollbar {
  width: 6px;
}
::-webkit-scrollbar-track {
  background: transparent;
}
::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 10px;
}
.dark ::-webkit-scrollbar-thumb {
  background: #334155;
}
</style>